import { useMutation, useQueryClient } from '@tanstack/react-query';

import { chatQueryKeys, ChatUnreadCount, markChatConversationRead } from 'services/api/chat';

/**
 * Mark a conversation read (OLIO-2834). The caller passes the newest message timestamp it
 * actually rendered — never "now" — so a message that arrived after the last fetch stays unread.
 * On success this locally zeroes the channel's row in BOTH caches (a conversation id is
 * globally unique): the counts cache — the sole counts source, so a later re-merge propagates
 * the zero instead of resurrecting the stale count — and the channel-list rows the badges
 * render from. The server cursor is advanced and the next counts refetch (focus / story-4
 * tick) confirms. Failure is silent: a missed cursor write just leaves the badge, and the
 * next view/refetch reconciles.
 */
export default function useMarkRead(conversationId: string) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (lastSeenAt: string) => markChatConversationRead(conversationId, lastSeenAt),
    onSuccess: () => {
      // Counts have ONE cache and one writer path — the badges derive from it at render
      // (useChatConversations merges), so zeroing here propagates everywhere.
      queryClient.setQueriesData<ChatUnreadCount[]>({ queryKey: chatQueryKeys.unreadCountsBase }, (existing) =>
        existing?.map((row) => (row.chatConversationId === conversationId ? { ...row, unreadCount: 0 } : row))
      );
    },
  });

  return { markRead: mutation.mutate };
}
